import {
  Alert,
  Box,
  Button,
  Chip,
  Icon,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePainelOcorrencias } from "../../api/ESocialJTServiceApi";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import ArquivarButton from "../../components/arquivar/ArquivarButton";
import FiltrosPessoas from "../../components/operacional/FiltrosPessoas";
import { EstadoConsulta } from "../../components/operacional/Elementos";
import {
  competenciaExibicao,
  cpfExibicao,
  dataExibicao,
  ESTADOS,
  TIPOS
} from "../../shared/apresentacaoEvento";
export default function EventosPage() {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const filtros = useMemo(
    () => ({
      ...Object.fromEntries(new URLSearchParams(key)),
      incluirArquivados:
        new URLSearchParams(key).get("incluirArquivados") === "true"
    }),
    [key]
  );
  const page = Math.max(0, Number(params.get("page")) || 0);
  const query = usePainelOcorrencias({ ...filtros, page, size: 25 });
  const { pagina: resultado = {}, contagemEstado = [] } = query.data || {};
  function aplicar(f) {
    const p = new URLSearchParams();
    Object.entries({ ...f, page: 0 }).forEach(([k, v]) => {
      if (v !== "" && v !== undefined && v !== false) p.set(k, String(v));
    });
    setParams(p);
  }
  function pagina(n) {
    const p = new URLSearchParams(params);
    p.set("page", String(n));
    setParams(p);
  }
  const count = (e) =>
    contagemEstado
      .filter((c) => e.includes(Number(c.estado)))
      .reduce((s, c) => s + Number(c.quantidade || c.total || 0), 0);
  return (
    <Page maxWidth="lg">
      <PageHeader
        primary="Ocorrências"
        secondary="Acompanhe os eventos, encontre pessoas e consulte os retornos do eSocial."
        commands={[
          <Button
            key="dash"
            component={Link}
            to="/dashboard"
            startIcon={<Icon>dashboard</Icon>}
          >
            Dashboard
          </Button>
        ]}
      />
      <FiltrosPessoas
        value={filtros}
        onSubmit={aplicar}
        busy={query.isFetching}
      />
      {(filtros.competencia || filtros.dataInicial) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label={
              filtros.competencia
                ? `Competência: ${competenciaExibicao(filtros.competencia)}`
                : `Recebimento: ${dataExibicao(
                    filtros.dataInicial
                  )} a ${dataExibicao(filtros.dataFinal)}`
            }
            onDelete={() =>
              aplicar({
                ...filtros,
                competencia: "",
                dataInicial: "",
                dataFinal: ""
              })
            }
            color="primary"
            variant="outlined"
            sx={{
              height: "auto",
              "& .MuiChip-label": { whiteSpace: "normal", py: 1 }
            }}
          />
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        {[
          [
            "Todas",
            "",
            contagemEstado.reduce(
              (s, c) => s + Number(c.quantidade || c.total || 0),
              0
            ),
            "primary",
            "layers"
          ],
          ["Em fila", "1", count([1]), "warning", "schedule"],
          ["Processando", "2", count([2]), "info", "sync"],
          ["Sucesso", "3", count([3]), "success", "check_circle"],
          ["Com erro", "4,5", count([4, 5]), "error", "error_outline"]
        ].map(([label, value, total, color, icon]) => (
          <Button
            key={label}
            onClick={() => aplicar({ ...filtros, estados: value })}
            aria-pressed={(filtros.estados || "") === value}
            color={color}
            variant={
              (filtros.estados || "") === value ? "contained" : "outlined"
            }
            disableElevation
            sx={{
              borderRadius: 1,
              minHeight: 76,
              flex: "1 1 150px",
              flexDirection: "column",
              textTransform: "none",
              ...((filtros.estados || "") === value && {
                color: "#fff",
                bgcolor:
                  color === "warning"
                    ? "#8a5700"
                    : color === "info"
                    ? "#006b78"
                    : `${color}.main`,
                "&:hover": {
                  bgcolor:
                    color === "warning"
                      ? "#704600"
                      : color === "info"
                      ? "#004f59"
                      : `${color}.dark`
                }
              }),
              ...((filtros.estados || "") !== value && {
                color: "text.secondary",
                borderColor: "divider",
                bgcolor: "background.paper"
              })
            }}
          >
            <Stack direction="row" spacing={0.7} alignItems="center">
              <Icon fontSize="small">{icon}</Icon>
              <span>{label}</span>
            </Stack>
            <Typography component="span" fontSize={23} fontWeight={700}>
              {new Intl.NumberFormat("pt-BR").format(total)}
            </Typography>
          </Button>
        ))}
      </Stack>
      <EstadoConsulta query={query}>
        {!resultado.content?.length ? (
          <Alert severity="info">
            Nenhuma ocorrência encontrada para os filtros selecionados.
          </Alert>
        ) : (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            <TableContainer>
              <Table aria-label="Ocorrências do eSocial">
                <TableHead>
                  <TableRow>
                    <TableCell>Evento / pessoa</TableCell>
                    <TableCell>CPF / matrícula</TableCell>
                    <TableCell>Competência</TableCell>
                    <TableCell>Situação</TableCell>
                    <TableCell>Recebimento</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultado.content.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>
                        <Typography
                          component={Link}
                          to={`/eventos/${o.id}?${key}`}
                          sx={{
                            fontWeight: 600,
                            color: "primary.main",
                            textDecoration: "none",
                            display: "block"
                          }}
                        >
                          S-{o.tipo || "—"} ·{" "}
                          {TIPOS[o.tipo] || o.tipoOcorrencia}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {o.nome || "Nome não disponível no cadastro local"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ocorrência #{o.id}
                          {o.arquivado ? " · Arquivada" : ""}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {cpfExibicao(o.cpf)}
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          Matrícula {o.matricula || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {competenciaExibicao(o.competencia)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={ESTADOS[o.estado] || "Gerando evento"}
                          color={
                            Number(o.estado) === 3
                              ? "success"
                              : [4, 5].includes(Number(o.estado))
                              ? "error"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {dataExibicao(o.dataRecebimento)}
                      </TableCell>
                      <TableCell>
                        <ArquivarButton ocorrencia={o} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </EstadoConsulta>
      {resultado.totalElements > 0 && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {resultado.totalElements} ocorrências encontradas
          </Typography>
          <Pagination
            count={resultado.totalPages || 1}
            page={page + 1}
            onChange={(_, v) => pagina(v - 1)}
            disabled={query.isFetching}
            color="primary"
          />
        </Box>
      )}
    </Page>
  );
}
